<script setup>
import Vue from 'vue'
import helpers from '@/lib/helpers'
import FileViewModal from './file-view-modal.vue'
import {fileIcon, scanLine} from './file-kind'

const props = defineProps({
  files: Array,
  noPublic: Boolean,
  /** just the name, size and a remove: a message's attachments */
  plain: Boolean,
})
const emit = defineEmits(['refresh'])
const refresh = () => emit('refresh')

/** the preview, with what the scan read beside it; a re-read there refreshes this list */
const preview = (file) => Vue.prototype.$dialog.show(FileViewModal, {url: `/api/file/${file.id}/meta`, onChange: refresh})

const copyExternal = (file) =>
  navigator.clipboard.writeText(file._links.external_url.href).then(() => helpers.toast('Copied link to clipboard.'))

// the list is the parent's (often filtered): a change is shown by reloading it there
const modify = async (url) => {
  await helpers.asyncAction(url.href, url.method)
  refresh()
}

const deleteFile = async (url) => {
  try {
    // taking a file off a message is undone by attaching it again: no question
    await helpers.asyncAction(url.href, url.method, {confirm: !props.plain})
  } catch (error) {
    return // not confirmed, or refused (the error is shown)
  }
  refresh()
}

const rescan = async (file) => {
  const r = await helpers.asyncAction(file._links.rescan_url.href, file._links.rescan_url.method)
  refresh()
  helpers.toast(r.scan.status === 'read' ? 'Read again' : 'Could not be read: ' + (r.scan.error || 'no text'))
}
</script>

<template>
  <div class="files-list" v-if="plain">
    <div :key="file.id" v-for="file in files" class="plain-row d-flex align-items-center">
      <font-awesome-icon :icon="fileIcon(file).icon" :class="'text-' + fileIcon(file).color" fixed-width class="mr-2 flex-shrink-0"/>
      <a href="#" class="text-truncate flex-fill min-w-0" @click.prevent="preview(file)" :title="file.orig_filename">{{ file.orig_filename }}</a>
      <span v-if="file.file_size" class="small text-muted ml-2 flex-shrink-0">{{ file.file_size | prettyBytes(0) }}</span>
      <b-button v-if="file._links.delete_url" variant="link" size="sm" class="text-secondary px-2 py-0 ml-1" title="Remove" @click="deleteFile(file._links.delete_url)">
        <font-awesome-icon icon="fa-solid fa-xmark"/>
      </b-button>
    </div>
  </div>
  <div class="files-list" v-else>
    <div :key="file.id" v-for="file in files" class="file-row d-flex align-items-start">
      <!-- what it is: a photo's thumbnail, a document's type icon -->
      <div class="file-icon flex-shrink-0 mr-3" @click="preview(file)" role="button" :title="'Preview ' + file.orig_filename">
        <img v-if="file.is_image && file.thumbs" :src="file.thumbs.small" alt="" class="file-thumb"/>
        <font-awesome-icon v-else :icon="fileIcon(file).icon" :class="'text-' + fileIcon(file).color" size="2x"/>
      </div>

      <div class="flex-fill min-w-0">
        <!-- the name has the line to itself: it wraps rather than being cut off -->
        <div class="d-flex align-items-start">
          <!-- the name, then its document type: the tag follows the name, wrapping with it -->
          <div class="flex-fill min-w-0">
            <a href="#" class="file-name" @click.prevent="preview(file)" :title="file.orig_filename">{{ file.orig_filename }}</a>
            <span v-if="file.scan && file.scan.doc_type_desc" class="badge badge-light border ml-1 align-text-bottom">{{ file.scan.doc_type_desc }}</span>
          </div>
          <div class="file-actions d-flex align-items-center flex-shrink-0 ml-2">
            <b-button variant="link" size="sm" class="text-secondary px-2 py-0" title="Download" @click="helpers.download(file.url, file.orig_filename)">
              <font-awesome-icon icon="fa-solid fa-download"/>
            </b-button>
            <b-dropdown variant="link" size="sm" right no-caret toggle-class="text-secondary px-2 py-0" title="More">
              <template #button-content>
                <font-awesome-icon icon="fa-solid fa-ellipsis-vertical"/>
              </template>
              <b-dropdown-item-button @click="preview(file)">
                <font-awesome-icon icon="fa-solid fa-eye" fixed-width class="mr-1"/>
                Preview
              </b-dropdown-item-button>
              <!-- the record's own actions for the file (an order's print with the shipment) -->
              <slot name="menu" :file="file" :refresh="refresh"/>
              <template v-if="!noPublic">
                <template v-if="file.public">
                  <b-dropdown-item-button @click="copyExternal(file)">
                    <font-awesome-icon icon="fa-solid fa-copy" fixed-width class="mr-1"/>
                    Copy public link
                  </b-dropdown-item-button>
                  <b-dropdown-item-button @click="modify(file._links.private_url)">
                    <font-awesome-icon icon="fa-solid fa-user-secret" fixed-width class="mr-1"/>
                    Make private
                  </b-dropdown-item-button>
                </template>
                <b-dropdown-item-button v-else @click="modify(file._links.public_url)">
                  <font-awesome-icon icon="fa-solid fa-globe-americas" fixed-width class="mr-1"/>
                  Make public
                </b-dropdown-item-button>
              </template>
              <b-dropdown-item-button v-if="file._links.rescan_url" @click="rescan(file)">
                <font-awesome-icon icon="fa-solid fa-rotate" fixed-width class="mr-1"/>
                Read again
              </b-dropdown-item-button>
              <template v-if="file._links.delete_url">
                <b-dropdown-divider/>
                <b-dropdown-item-button variant="danger" @click="deleteFile(file._links.delete_url)">
                  <font-awesome-icon icon="fa-solid fa-trash" fixed-width class="mr-1"/>
                  Delete
                </b-dropdown-item-button>
              </template>
            </b-dropdown>
          </div>
        </div>

        <!-- what the scan read: a line about it -->
        <div v-if="file.scan" class="file-summary mt-1">
          <span v-if="file.scan.summary">{{ file.scan.summary }}</span>
          <span v-else-if="file.scan.status === 'pending'" class="text-muted font-italic">Reading…</span>
          <span v-else-if="file.scan.status === 'failed'" class="text-muted font-italic" :title="file.scan.error">No text could be read</span>
        </div>

        <!-- who it is from, its number and date; then the file's size and who added it -->
        <div v-if="scanLine(file)" class="file-meta mt-1">{{ scanLine(file) }}</div>
        <div class="file-meta">
          <span v-if="!noPublic && file.public" class="badge badge-warning mr-1">Public</span>
          <template v-if="file.file_size">{{ file.file_size | prettyBytes(0) }} · </template>
          <span :title="file.created_datetime">added {{ file.created_datetime | timediff }}</span>
          <template v-if="file.createdby_user"> by <span :title="file.createdby_user.name">{{ file.createdby_user.userid }}</span></template>
        </div>

        <!-- the record's own controls for the file (an order's Print and its instructions) -->
        <div class="d-flex flex-wrap align-items-center file-extra">
          <slot name="info" :file="file" :refresh="refresh"/>
          <div class="btn-group btn-group-sm">
            <slot name="button" :file="file" :refresh="refresh"/>
          </div>
        </div>
        <slot name="more_info" :refresh="refresh" :file="file"/>
      </div>
    </div>
  </div>
</template>

<style scoped>
.plain-row {
  padding: .4rem .75rem;
  border-bottom: 1px solid rgba(0, 0, 0, .06);
}

.file-row {
  padding: .85rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, .08);
}

.file-row:hover {
  background: rgba(0, 0, 0, .02);
}

.file-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
}

.min-w-0 {
  min-width: 0;
}

.file-name {
  font-weight: 600;
  line-height: 1.3;
  word-break: break-word;
}

.file-summary {
  font-size: .875rem;
  line-height: 1.35;
  color: #495057;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.file-meta {
  font-size: .78rem;
  color: #6c757d;
  line-height: 1.4;
}

/* the record's controls sit under the details, apart from them */
.file-extra .btn-group:not(:empty) {
  margin-top: .4rem;
}

.file-extra .btn-group:empty {
  display: none;
}
</style>
